import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import { styled } from "@mui/system";
import React, { useContext } from "react";
import AccountPeopleIcon from "src/icons/AccountPeopleIcon";
import ContainedButton from "../common/ContainedButton";
import FilterTabButton from "../raf/FilterTab/FilterTabButton";
import ProfileHeaderModal from "../raf/Profile/ProfileHeaderModal";
import MultiAccountListV2 from "./MultiAccountListV2";
import NotificationList from "./NotificationList";
import { Dialog, Popover, Skeleton } from "@mui/material";
import { setNotifications, useUserSlice } from "src/store/features/user.slice";
import api from "src/store/features/api";
import AuthContext from "src/providers/AuthProvider";
import { NotificationRes } from "src/store/features/api/generated";
import { useAppDispatch } from "src/store/store";
import ProfileInitials from "../raf/Profile/ProfileInitials";
import { trackEvent } from "src/Amplitude";

import { useTSAccountProtection } from "@transmitsecurity/riskid-reactjs-ts";
import Preloader from "src/utils/Preloader";
const Wrapper = styled("div")(({ theme }) => ({
  display: "flex",
  width: "440px",
  padding: "24px",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "16px",
  borderRadius: "8px",
  background: "#FFF",

  [theme.breakpoints.down(900)]: {
    width: "100%",
    padding: "0px",
  },
}));

const TabsWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  width: "100%",
  alignItems: "flex-start",
  gap: "8px",

  [theme.breakpoints.down(900)]: {
    width: "100%",
  },
}));

const ContentWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  width: "100%",

  [theme.breakpoints.down(900)]: {
    width: "100%",
    padding: "0px 16px",
  },
}));

const NotificationListWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "8px",
  alignSelf: "stretch",

  [theme.breakpoints.down(900)]: {
    padding: "0px 16px",
  },
}));

interface DividerProps {
  hide: boolean;
}
const Divider = styled("div")<DividerProps>(({ hide }) => ({
  display: hide ? "none" : "flex",
  width: "100%",
  minHeight: "1px",
  background: "#E5E7E8",
}));

const LogoutLoader = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: "24px",
});

const AccountLockBannerWrapper = styled("div")({
  display: "flex",
  padding: "12px",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  gap: "8px",
  alignSelf: "stretch",
  borderRadius: "10px",
  background: "#F8F9F9",
});
const AccountLockBannerHeadingText = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  alignSelf: "stretch",
  color: "#002035",
  fontFamily: "Roobert",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: "500",
  lineHeight: "16px",
  textDecorationLine: "underline",
  textDecorationStyle: "solid",
  textDecorationSkipInk: "none",
  textDecorationThickness: "auto",
  textUnderlineOffset: "auto",
  textUnderlinePosition: "from-font",
});
const AccountLockBannerText = styled("div")({
  display: "flex",
  minWidth: "247px",
  alignItems: "center",
  gap: "8px",
  flex: "1 0 0",
  color: "#002035",
  fontFamily: "Roobert",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: "400",
  lineHeight: "16px",
});

interface ProfileModalProps {
  fullscreen?: boolean;
  ProfileInitialsScale?: string;
}
const ProfileModal = (props: ProfileModalProps) => {
  const { fullscreen, ProfileInitialsScale } = props;
  const {
    raf: { tier },
    details: { customerId, name },
    accountLock,
    notifications: {
      currentNotifications,
      previousNotifications,
      notificationAmount,
      refetchNotifications,
    },
  } = useUserSlice();
  const { logout } = useContext(AuthContext);
  const dispatch = useAppDispatch();

  const { data: multiAccountList } = api.useGetMultiAccountUsersQuery();
  const { data: unreadNotifications } = api.useGetUnreadNotificationsQuery(
    {
      read: false,
    },
    {
      pollingInterval: 60000, // 60 seconds
    },
  ); // "current" notifications
  const { data: readNotifications } = api.useGetUnreadNotificationsQuery({ read: true }); // "previous" notifications
  const { clearUser } = useTSAccountProtection();
  React.useEffect(() => {
    if (!unreadNotifications || refetchNotifications) return () => {};
    dispatch(
      setNotifications({
        currentNotifications: unreadNotifications.data,
        notificationAmount: unreadNotifications.data.length,
        refetchNotifications: false,
      }),
    );
  }, [unreadNotifications]);

  React.useEffect(() => {
    let previousNotificationList: NotificationRes[] = [];
    if (!readNotifications) return () => {};
    previousNotificationList = readNotifications.data.filter((notification) => notification.read);
    dispatch(setNotifications({ previousNotifications: previousNotificationList }));
  }, [readNotifications]);

  const [selectedTab, setSelectedTab] = React.useState<"accounts" | "notifications">("accounts");
  const handleTabChange = (tab: "accounts" | "notifications") => {
    setSelectedTab(tab);
  };

  const [isLoggingOut, setIsLoggingOut] = React.useState<boolean>(false);
  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    await clearUser();
    await logout();
  };

  // POPPER LOGIC
  //------------- FUNCTIONS -------------
  const initials = React.useMemo((): string => {
    if (!name) return "";
    // Split the name into an array of words
    const words = name.split(" ");

    // Initialize an empty string to store initials
    let initials = "";

    // Iterate over each word in the array
    for (const word of words) {
      // Get the first character of each word and convert it to uppercase
      initials += word.charAt(0).toUpperCase();
    }

    // Return the initials
    return initials.slice(0, 2);
  }, [name]);

  const [openDialog, setOpenDialog] = React.useState<boolean>(false);

  const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | null>(null);
  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  const handleProfileModalToggle = (event: React.MouseEvent<HTMLDivElement>) => {
    trackEvent("Select_Profile_Button", {}, "Header");
    setOpenDialog(true);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setAnchorEl(null);
    // avoid modal flicker when closing
    setTimeout(() => setSelectedTab("accounts"), 200);
  };

  const AccountLockBanner = ({ timestamp }: { timestamp: number }) => {
    return (
      <AccountLockBannerWrapper>
        <AccountLockBannerHeadingText>
          Account locked on {new Date(timestamp).toLocaleString()}
        </AccountLockBannerHeadingText>
        <AccountLockBannerText>
          To regain access to features such as placing orders, making withdrawals, and allocating
          deposits, please contact us at support@calebandbrown.com for assistance in unlocking your
          account.
        </AccountLockBannerText>
      </AccountLockBannerWrapper>
    );
  };

  return (
    <div
      style={{
        position: "relative",
        top: 0,
        right: 0,
      }}>
      {initials ? (
        <ProfileInitials
          initials={initials}
          tier={tier ?? "bronze"}
          notification={notificationAmount > 0}
          accountLocked={accountLock?.locked ?? false}
          onClick={handleProfileModalToggle}
          scale={ProfileInitialsScale}
        />
      ) : (
        <Skeleton
          variant="circular"
          width={45}
          height={45}
          sx={{ backgroundColor: "#FFFFFF", opacity: 1 }}
        />
      )}

      {fullscreen ? (
        <Dialog
          open={openDialog}
          onClose={handleClose}
          fullScreen={fullscreen}
          sx={{ background: "rgba(0, 32, 53, 0.7)" }}>
          <Wrapper>
            {/* RaF tier profile banner */}
            <ProfileHeaderModal
              name={name}
              customerID={customerId}
              tier={tier}
              accountLocked={accountLock?.locked ?? false}
              onClick={handleClose}
            />

            <ContentWrapper>
              {/* Select Buttons | accounts - notifications */}
              <TabsWrapper>
                <FilterTabButton
                  selected={selectedTab === "accounts"}
                  onClick={() => handleTabChange("accounts")}
                  text="Accounts"
                  icon={<AccountPeopleIcon color="#002035" />}
                />
                <FilterTabButton
                  selected={selectedTab === "notifications"}
                  onClick={() => handleTabChange("notifications")}
                  notification={notificationAmount > 0}
                  text="Notifications"
                  icon={<NotificationsNoneOutlinedIcon sx={{ width: "12px", height: "12px" }} />}
                />
              </TabsWrapper>

              {accountLock?.locked && <AccountLockBanner timestamp={accountLock.timestamp} />}

              {/* Multi-account select list */}
              {selectedTab === "accounts" && multiAccountList && (
                <MultiAccountListV2 accountList={multiAccountList} />
              )}

              {/* Notifications list | current - previous */}
              {selectedTab === "notifications" && (
                <NotificationListWrapper>
                  <NotificationList type="current" notifications={currentNotifications} />
                  <Divider hide={notificationAmount <= 0} />
                  <NotificationList type="previous" notifications={previousNotifications} />
                </NotificationListWrapper>
              )}

              {/* Logout button */}
              <ContainedButton color="salmon" width="100%" height="46px" onClick={handleLogout}>
                Logout
              </ContainedButton>
            </ContentWrapper>
          </Wrapper>
        </Dialog>
      ) : (
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          slotProps={{
            paper: {
              style: {
                boxShadow:
                  "0px 5px 5px -3px rgba(173, 192, 203, 0.3), 0px 8px 10px 1px rgba(173, 192, 203, 0.3), 0px 3px 14px 2px rgba(173, 192, 203, 0.3)",
              },
            },
          }}
          sx={{
            position: "absolute",
            top: 10,
            left: 10,
            maxHeight: "650px",
          }}>
          <Wrapper>
            {/* RaF tier profile banner */}
            <ProfileHeaderModal
              name={name}
              customerID={customerId}
              tier={tier}
              accountLocked={accountLock?.locked ?? false}
              onClick={handleClose}
            />

            <ContentWrapper>
              {/* Select Buttons | accounts - notifications */}
              <TabsWrapper>
                <FilterTabButton
                  selected={selectedTab === "accounts"}
                  onClick={() => handleTabChange("accounts")}
                  // onClick={() => handleTabChange("accounts")}
                  text="Accounts"
                  icon={<AccountPeopleIcon color="#002035" />}
                />
                <FilterTabButton
                  selected={selectedTab === "notifications"}
                  onClick={() => handleTabChange("notifications")}
                  notification={currentNotifications.length > 0}
                  text="Notifications"
                  icon={<NotificationsNoneOutlinedIcon sx={{ width: "12px", height: "12px" }} />}
                />
              </TabsWrapper>

              {accountLock?.locked && <AccountLockBanner timestamp={accountLock.timestamp} />}

              {/* Multi-account select list */}
              {selectedTab === "accounts" && multiAccountList && (
                <MultiAccountListV2 accountList={multiAccountList} />
              )}

              {/* Notifications list | current - previous */}
              {selectedTab === "notifications" && (
                <NotificationListWrapper>
                  <NotificationList type="current" notifications={currentNotifications} />
                  <Divider hide={currentNotifications.length <= 0} />
                  <NotificationList type="previous" notifications={previousNotifications} />
                </NotificationListWrapper>
              )}

              {/* Logout button */}
              <ContainedButton color="salmon" width="100%" height="46px" onClick={handleLogout}>
                {isLoggingOut ? (
                  <LogoutLoader>
                    Logging out
                    <Preloader size={"20px"} color="#FFF" />
                  </LogoutLoader>
                ) : (
                  "Logout"
                )}
              </ContainedButton>
            </ContentWrapper>
          </Wrapper>
        </Popover>
      )}
    </div>
  );
};

export default ProfileModal;
